import { useId } from 'react';
import { Link } from '@tanstack/react-router';

import { insideNativeApp } from '../auth/native-app';
import { isOwner } from '../auth/roles';
import { useSession } from '../auth/useSession';
import { PageHeader } from '../components/PageHeader';
import { LOOKS, LOOK_DESCRIPTION, LOOK_LABEL, type Look } from '../lib/look';
import { setLook, useLook } from '../lib/lookStore';
import { withViewTransition } from '../lib/motion/viewTransition';
import { PLAN_PITCH, planLabel } from '../lib/plan';
import { useBusiness } from '../lib/useBusiness';

/** The account's own settings, opened from the profile at the foot of the sidebar. */
export function ConfiguracionPage() {
  const look = useLook();
  const business = useBusiness();
  const { session } = useSession();
  const lookHeadingId = useId();
  const planHeadingId = useId();
  const plan = business?.plan ?? 'base';

  return (
    <main>
      <PageHeader title="Configuración" description="Cómo se ve el panel en este dispositivo, y tu plan." />

      <section className="section" aria-labelledby={lookHeadingId}>
        <h2 className="section-label" id={lookHeadingId}>Apariencia</h2>
        <div className="look-options" role="radiogroup" aria-labelledby={lookHeadingId}>
          {LOOKS.map((option) => (
            <label key={option} className="look-option" data-selected={look === option || undefined}>
              <input
                type="radio"
                name="look"
                className="look-radio"
                aria-label={LOOK_LABEL[option]}
                checked={look === option}
                onChange={() => withViewTransition(() => setLook(option))}
              />
              <LookPreview look={option} />
              <span className="look-option-name">{LOOK_LABEL[option]}</span>
              <span className="look-option-desc">{LOOK_DESCRIPTION[option]}</span>
            </label>
          ))}
        </div>
        <p className="row-sub look-options-note">Se guarda en este dispositivo. No cambia nada de lo que hace el panel.</p>
      </section>

      {isOwner(session?.role) && business ? (
        <section className="section" aria-labelledby={planHeadingId}>
          <h2 className="section-label" id={planHeadingId}>Tu plan</h2>
          <div className="group rows">
            <div className="row">
              <div className="grow">
                <p className="row-title">{planLabel(plan)}</p>
                <p className="row-sub">{PLAN_PITCH[plan]}</p>
              </div>
              {insideNativeApp() ? null : <Link to="/membresia" className="btn small secondary">Cambiar de plan</Link>}
            </div>
          </div>
        </section>
      ) : null}
    </main>
  );
}

/** A thumbnail of each look: its paper, its sidebar, a card and its primary button, drawn in that look's own colors. */
function LookPreview({ look }: { look: Look }) {
  return (
    <span className="look-preview" data-preview={look} aria-hidden="true">
      <span className="look-preview-side" />
      <span className="look-preview-main">
        <span className="look-preview-title">Aa</span>
        <span className="look-preview-card" />
        <span className="look-preview-button" />
      </span>
    </span>
  );
}
